import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import Title from '../../../components/Title';
import useAxiosInterceptor from '../../AxiosInterceptor';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { shouldRenderForbiddenAction } from '../../../utils/permission-ui';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type NavigationMenu = {
  id: number;
  name: string;
  route: string;
  orden: number;
  icon: string;
  permissions?: {
    id: number;
    name: string;
    action: string;
  }[];
};

type NavigationModule = {
  id: number;
  name: string;
  order: number;
  icon: string;
  visible: boolean;
  menu: NavigationMenu[];
};

type ModuleFormState = {
  id?: number;
  name: string;
  order: string;
  icon: string;
  visible: boolean;
};

type MenuFormState = {
  id?: number;
  name: string;
  route: string;
  orden: string;
  icon: string;
  actions: string[];
};

type MenuActionOption = {
  action: string;
  label: string;
};

const MENU_ACTION_OPTIONS: MenuActionOption[] = [
  { action: 'read', label: 'Consultar' },
  { action: 'create', label: 'Crear' },
  { action: 'update', label: 'Editar' },
  { action: 'delete', label: 'Eliminar' },
  { action: 'assign', label: 'Asignar' },
  { action: 'manage_mobile', label: 'Gestionar' },
  { action: 'delete_box_movement', label: 'Eliminar movimiento' },
  { action: 'delete_expense_movement', label: 'Eliminar movimiento' },
  { action: 'show_location', label: 'Mostrar ubicación' },
  { action: 'allow_manual_discount', label: 'Descuento manual' },
  { action: 'allow_automatic_discount', label: 'Descuento regla automática' },
  { action: 'pay', label: 'Abono' },
  { action: 'refinance', label: 'Refinanciar' },
  { action: 'update_constant', label: 'Modificar constante' },
  { action: 'delete_selected', label: 'Eliminar seleccionados' },
  { action: 'pdf', label: 'PDF' },
  { action: 'adjust_box', label: 'Ajustar caja' },
  { action: 'register_expenses', label: 'Registrar gastos' },
  { action: 'export', label: 'Exportar' },
  { action: 'view_errors', label: 'Ver errores' },
  { action: 'transfer', label: 'Transferir' },
  { action: 'update_quote', label: 'Modificar cupo' },
  { action: 'manage_backup_alert', label: 'Gestionar alerta' },
  { action: 'toggle_enabled', label: 'Habilitar / deshabilitar' },
] ;

const MENU_ACTION_LABELS = MENU_ACTION_OPTIONS.reduce<Record<string, string>>((acc, item) => {
  acc[item.action] = item.label;
  return acc;
}, { toggle_visibility: 'Visualizar' });

const dedupePermissionActions = <T extends { action: string }>(permissions: T[] = []) => {
  const seenActions = new Set<string>();
  return permissions.filter((permission) => {
    const action = String(permission.action || '').toLowerCase();
    if (!action || seenActions.has(action)) {
      return false;
    }
    seenActions.add(action);
    return true;
  });
};

const normalizeMenuRoute = (route?: string | null) => {
  const trimmed = String(route || '').trim();
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return normalized.toLowerCase();
};

const emptyModuleForm: ModuleFormState = {
  name: '',
  order: '',
  icon: 'ViewModuleIcon',
  visible: true,
};

const emptyMenuForm: MenuFormState = {
  name: '',
  route: '',
  orden: '',
  icon: 'ViewModuleIcon',
  actions: [],
};

const PrivilegiosAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  document.title = 'Administrar Menu';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/privilegios');
  const showCreateActions = shouldRenderForbiddenAction(canCreate);
  const showUpdateActions = shouldRenderForbiddenAction(canUpdate);
  const showDeleteActions = shouldRenderForbiddenAction(canDelete);

  const [modules, setModules] = useState<NavigationModule[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [isLoadingModules, setIsLoadingModules] = useState(true);

  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleForm, setModuleForm] = useState<ModuleFormState>(emptyModuleForm);

  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [menuForm, setMenuForm] = useState<MenuFormState>(emptyMenuForm);

  const selectedModule = useMemo(
    () => modules.find((module) => module.id === selectedModuleId) ?? null,
    [modules, selectedModuleId]
  );
  const availableMenuActionOptions = useMemo(() => {
    const normalizedRoute = normalizeMenuRoute(menuForm.route);
    return MENU_ACTION_OPTIONS.flatMap((option) => {
      if (option.action === 'read' && normalizedRoute === '/administrar/usuarios') {
        return [{ ...option, label: 'Ver asociaciones' }];
      }
      if (option.action === 'read' && normalizedRoute === '/prestamos/abonosporfecha') {
        return [{ ...option, label: 'Mostrar historial' }];
      }
      if (option.action === 'create' && normalizedRoute === '/prestamos/abonosporfecha') {
        return [];
      }
      if (option.action === 'create' && normalizedRoute === '/prestamos/modificarabono') {
        return [];
      }
      if (option.action === 'read' && normalizedRoute === '/prestamos/modificarabono') {
        return [];
      }
      if (option.action === 'assign') {
        if (normalizedRoute === '/administrar/constantes') {
          return [{ ...option, label: 'Asignar' }];
        }
        if (normalizedRoute === '/administrar/empleados') {
          return [{ ...option, label: 'Asignar cobro' }];
        }
        return [];
      }
      if (option.action === 'manage_mobile') {
        return normalizedRoute === '/administrar/empleados'
          ? [{ ...option, label: 'Gestionar mobile' }]
          : [];
      }
      if (option.action === 'delete_box_movement') {
        return normalizedRoute === '/administrar/cajas/add'
          ? [{ ...option, label: 'Eliminar movimiento caja principal' }]
          : [];
      }
      if (option.action === 'delete_expense_movement') {
        return normalizedRoute === '/administrar/detallegasto'
          ? [{ ...option, label: 'Eliminar movimiento gastos' }]
          : [];
      }
      if (option.action === 'show_location') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Mostrar ubicación' }]
          : [];
      }
      if (option.action === 'allow_manual_discount') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Descuento manual' }]
          : [];
      }
      if (option.action === 'allow_automatic_discount') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Descuento regla automática' }]
          : [];
      }
      if (option.action === 'pay') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Abono' }]
          : [];
      }
      if (option.action === 'refinance') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Refinanciar' }]
          : [];
      }
      if (option.action === 'update_constant') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Modificar constante' }]
          : [];
      }
      if (option.action === 'delete_selected') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'Eliminar seleccionados' }]
          : [];
      }
      if (option.action === 'pdf') {
        return normalizedRoute === '/prestamos/abonos/list'
          ? [{ ...option, label: 'PDF' }]
          : [];
      }
      if (option.action === 'adjust_box') {
        return normalizedRoute === '/prestamos/gastos/add'
          ? [{ ...option, label: 'Ajustar caja' }]
          : [];
      }
      if (option.action === 'register_expenses') {
        return normalizedRoute === '/prestamos/gastos/add' || normalizedRoute === '/procesos/liquidacioncobros'
          ? [{ ...option, label: 'Registrar gastos' }]
          : [];
      }
      if (option.action === 'export') {
        return normalizedRoute === '/procesos/exportarclientes'
          ? [{ ...option, label: 'Exportar' }]
          : [];
      }
      if (option.action === 'view_errors') {
        return normalizedRoute === '/procesos/importarprestamo'
          ? [{ ...option, label: 'Ver errores' }]
          : [];
      }
      if (option.action === 'transfer') {
        return normalizedRoute === '/procesos/transferirprestamo'
          ? [{ ...option, label: 'Transferir' }]
          : [];
      }
      if (option.action === 'update_quote') {
        return normalizedRoute === '/administrar/clientes' || normalizedRoute === '/informes/historicocliente'
          ? [{ ...option, label: 'Modificar cupo' }]
          : [];
      }
      if (option.action === 'manage_backup_alert') {
        return normalizedRoute === '/administrar/clientes' || normalizedRoute === '/informes/historicocliente'
          ? [{ ...option, label: 'Gestionar alerta' }]
          : [];
      }
      if (normalizedRoute === '/administrar/clientes') {
        if (option.action === 'read') return [{ ...option, label: 'Ver cambios' }];
      }
      if (normalizedRoute === '/informes/reporteprestamo') {
        if (option.action === 'read') return [{ ...option, label: 'Historial' }];
      }
      if (normalizedRoute === '/procesos/rutero') {
        if (option.action === 'update') return [{ ...option, label: 'Guardar' }];
        return [];
      }
      if (normalizedRoute === '/procesos/liquidacioncobros') {
        if (option.action === 'create') return [{ ...option, label: 'Liquidar' }];
        if (option.action === 'update') return [{ ...option, label: 'Reliquidar' }];
        if (option.action === 'delete') return [{ ...option, label: 'Eliminar gastos' }];
        if (option.action === 'register_expenses') return [{ ...option, label: 'Registrar gastos' }];
        return [];
      }
      if (normalizedRoute === '/procesos/ruta-seguimiento') {
        if (option.action === 'read') return [{ ...option, label: 'Consultar' }];
        if (option.action === 'update') return [{ ...option, label: 'Actualizar ruta' }];
        return [];
      }
      if (normalizedRoute === '/procesos/exportarclientes') {
        return [];
      }
      if (normalizedRoute === '/procesos/importarprestamo') {
        if (option.action === 'read') return [{ ...option, label: 'Historial' }];
        if (option.action === 'create') return [{ ...option, label: 'Guardar' }];
        if (option.action === 'view_errors') return [{ ...option, label: 'Ver errores' }];
        return [];
      }
      if (normalizedRoute === '/procesos/importaciones') {
        if (option.action === 'read') return [{ ...option, label: 'Consultar' }];
        if (option.action === 'delete_selected') return [{ ...option, label: 'Eliminar seleccionados' }];
        if (option.action === 'delete') return [{ ...option, label: 'Eliminar' }];
        return [];
      }
      if (normalizedRoute === '/procesos/auditoria-sincronizacion') {
        if (option.action === 'read') return [{ ...option, label: 'Consultar' }];
        return [];
      }
      if (normalizedRoute === '/procesos/transferirprestamo') {
        return [];
      }
      if (normalizedRoute === '/prestamos/actualizarterminal') {
        if (option.action === 'update') return [{ ...option, label: 'Guardar' }];
        return [];
      }
      if (normalizedRoute === '/informes/historicocliente') {
        if (option.action === 'read') return [{ ...option, label: 'Historial' }];
        if (option.action === 'update_quote') return [{ ...option, label: 'Modificar cupo' }];
        if (option.action === 'manage_backup_alert') return [{ ...option, label: 'Gestionar alerta' }];
        return [];
      }
      if (normalizedRoute === '/informes/reportegastos') {
        if (option.action === 'delete') return [{ ...option, label: 'Eliminar' }];
        return [];
      }
      if (normalizedRoute === '/reportes/reportecajas') {
        if (option.action === 'read') return [{ ...option, label: 'Ver detalle de cajas' }];
        return [option];
      }
      return [option];
    });
  }, [menuForm.route]);
  const canSaveModule = moduleForm.id ? canUpdate : canCreate;
  const canSaveMenu = menuForm.id ? canUpdate : canCreate;
  const showModuleSaveAction = shouldRenderForbiddenAction(canSaveModule);
  const showMenuSaveAction = shouldRenderForbiddenAction(canSaveMenu);

  const loadModules = async () => {
    const response = await axios.get('modules/navigation');
    const items: NavigationModule[] = response.data?.data ?? [];
    setModules(items);

    if (items.length === 0) {
      setSelectedModuleId(null);
      return;
    }

    setSelectedModuleId((current) => {
      if (current && items.some((module) => module.id === current)) {
        return current;
      }
      return items[0].id;
    });
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setIsLoadingModules(true);
        await loadModules();
      } catch (error: any) {
        console.error('Error cargando administración de menú', error);
        const message = error?.response?.data?.message || 'Error al cargar módulos y menús';
        showAlert(message, 'error');
      } finally {
        setIsLoadingModules(false);
      }
    };

    bootstrap();
  }, [showAlert]);

  const openCreateModuleDialog = () => {
    setModuleForm(emptyModuleForm);
    setModuleDialogOpen(true);
  };

  const openEditModuleDialog = (module: NavigationModule) => {
    setModuleForm({
      id: module.id,
      name: module.name,
      order: String(module.order),
      icon: module.icon,
      visible: Boolean(module.visible),
    });
    setModuleDialogOpen(true);
  };

  const closeModuleDialog = () => {
    setModuleDialogOpen(false);
    setModuleForm(emptyModuleForm);
  };

  const openCreateMenuDialog = () => {
    if (!selectedModuleId) {
      showAlert('Selecciona un módulo primero', 'warning');
      return;
    }
    setMenuForm(emptyMenuForm);
    setMenuDialogOpen(true);
  };

  const openEditMenuDialog = (menu: NavigationMenu) => {
    setMenuForm({
      id: menu.id,
      name: menu.name,
      route: menu.route,
      orden: String(menu.orden),
      icon: menu.icon,
      actions: dedupePermissionActions(menu.permissions || [])
        .map((permission) => permission.action)
        .filter((action) => action !== 'toggle_visibility'),
    });
    setMenuDialogOpen(true);
  };

  const closeMenuDialog = () => {
    setMenuDialogOpen(false);
    setMenuForm(emptyMenuForm);
  };

  const submitModule = async () => {
    const payload = {
      name: moduleForm.name.trim(),
      order: moduleForm.order === '' ? undefined : Number(moduleForm.order),
      icon: moduleForm.icon.trim(),
      visible: moduleForm.visible,
    };

    if (!payload.name) {
      showAlert('El nombre del módulo es obligatorio', 'warning');
      return;
    }

    try {
      showLoading();
      if (moduleForm.id) {
        await axios.put(`modules/${moduleForm.id}`, payload);
        showAlert('Módulo actualizado correctamente', 'success');
      } else {
        await axios.post('modules', payload);
        showAlert('Módulo creado correctamente', 'success');
      }
      await loadModules();
      closeModuleDialog();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'No fue posible guardar el módulo';
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const submitMenu = async () => {
    if (!selectedModuleId) {
      showAlert('Selecciona un módulo primero', 'warning');
      return;
    }

    const payload = {
      moduleId: selectedModuleId,
      name: menuForm.name.trim(),
      route: menuForm.route.trim(),
      orden: menuForm.orden === '' ? undefined : Number(menuForm.orden),
      icon: menuForm.icon.trim(),
      actions: menuForm.actions,
    };

    if (!payload.name || !payload.route) {
      showAlert('Nombre y ruta del menú son obligatorios', 'warning');
      return;
    }

    try {
      showLoading();
      if (menuForm.id) {
        await axios.put(`menus/${menuForm.id}`, payload);
        showAlert('Menú actualizado correctamente', 'success');
      } else {
        await axios.post('menus', payload);
        showAlert('Menú creado correctamente', 'success');
      }
      await loadModules();
      closeMenuDialog();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'No fue posible guardar el menú';
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const toggleMenuAction = (action: string) => {
    setMenuForm((prev) => {
      const nextActions = prev.actions.includes(action)
        ? prev.actions.filter((item) => item !== action)
        : [...prev.actions, action];

      return {
        ...prev,
        actions: nextActions,
      };
    });
  };

  const removeModule = async (moduleId: number) => {
    if (!canDelete) return;

    if (!window.confirm('¿Seguro que deseas eliminar este módulo?')) return;

    try {
      showLoading();
      await axios.delete(`modules/${moduleId}`);
      showAlert('Módulo eliminado correctamente', 'success');
      await loadModules();
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || 'No fue posible eliminar el módulo';

      if (status === 409) {
        const confirmForce = window.confirm(`${message}\n\n¿Deseas eliminar en cascada (force=true)?`);
        if (!confirmForce) {
          hideLoading();
          return;
        }

        try {
          await axios.delete(`modules/${moduleId}`, { params: { force: true } });
          showAlert('Módulo eliminado en cascada', 'success');
          await loadModules();
        } catch (forceError: any) {
          const forceMessage = forceError?.response?.data?.message || 'No fue posible eliminar en cascada';
          showAlert(forceMessage, 'error');
        }
      } else {
        showAlert(message, 'error');
      }
    } finally {
      hideLoading();
    }
  };

  const removeMenu = async (menuId: number) => {
    if (!canDelete) return;

    if (!window.confirm('¿Seguro que deseas eliminar este menú?')) return;

    try {
      showLoading();
      await axios.delete(`menus/${menuId}`);
      showAlert('Menú eliminado correctamente', 'success');
      await loadModules();
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || 'No fue posible eliminar el menú';

      if (status === 409) {
        const confirmForce = window.confirm(`${message}\n\n¿Deseas eliminar en cascada (force=true)?`);
        if (!confirmForce) {
          hideLoading();
          return;
        }

        try {
          await axios.delete(`menus/${menuId}`, { params: { force: true } });
          showAlert('Menú eliminado en cascada', 'success');
          await loadModules();
        } catch (forceError: any) {
          const forceMessage = forceError?.response?.data?.message || 'No fue posible eliminar en cascada';
          showAlert(forceMessage, 'error');
        }
      } else {
        showAlert(message, 'error');
      }
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
        mb={2}
      >
        <Title>Administrar módulos y menús</Title>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', sm: 'auto' }}>
          <Button variant="outlined" component={Link} to="/administrar/privilegios">
            Regresar
          </Button>
          {showCreateActions && (
            <Tooltip title={canCreate ? 'Crear módulo' : 'Sin permiso para crear'}>
              <span>
                <Button
                  variant="contained"
                  startIcon={isMobile ? null : <AddIcon />}
                  onClick={openCreateModuleDialog}
                  disabled={!canCreate}
                  fullWidth={isMobile}
                >
                  {isMobile ? <AddIcon /> : 'Nuevo módulo'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Módulos</Typography>
                <Typography variant="caption" color="text.secondary">
                  {modules.length} registros
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {isLoadingModules ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : modules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay módulos creados.
                </Typography>
              ) : (
                <List dense sx={{ py: 0, maxHeight: { xs: '32vh', md: '54vh' }, overflowY: 'auto' }}>
                  {modules.map((module) => (
                    <ListItemButton
                      key={module.id}
                      selected={module.id === selectedModuleId}
                      onClick={() => setSelectedModuleId(module.id)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={module.name}
                        secondary={`Orden ${module.order} · ${module.menu?.length ?? 0} menús`}
                      />
                      {showUpdateActions && (
                        <Tooltip title={canUpdate ? 'Editar módulo' : 'Sin permiso para editar'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditModuleDialog(module);
                              }}
                              disabled={!canUpdate}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {showDeleteActions && (
                        <Tooltip title={canDelete ? 'Eliminar módulo' : 'Sin permiso para eliminar'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                removeModule(module.id);
                              }}
                              disabled={!canDelete}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                display="flex"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={1}
                mb={1}
              >
                <Typography variant="h6">
                  Menús {selectedModule ? `· ${selectedModule.name}` : ''}
                </Typography>
                {showCreateActions && (
                  <Tooltip title={canCreate ? 'Crear menú' : 'Sin permiso para crear'}>
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<SettingsApplicationsIcon />}
                        onClick={openCreateMenuDialog}
                        disabled={!canCreate || !selectedModule}
                        fullWidth={isMobile}
                      >
                        Nuevo menú
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {isLoadingModules ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : !selectedModule ? (
                <Typography variant="body2" color="text.secondary">
                  Selecciona un módulo para administrar sus menús.
                </Typography>
              ) : selectedModule.menu?.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Este módulo no tiene menús creados.
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.25}>
                  {selectedModule.menu.map((menu) => (
                    <Box
                      key={menu.id}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 1.5,
                        p: 1.25,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {menu.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {menu.route}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Orden: {menu.orden} · Ícono: {menu.icon}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Acciones: {dedupePermissionActions(menu.permissions || [])
                          .map((permission) => MENU_ACTION_LABELS[permission.action] || permission.name)
                          .join(', ') || 'Visualizar'}
                      </Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" mt={0.5}>
                        {showUpdateActions && (
                          <Tooltip title={canUpdate ? 'Editar menú' : 'Sin permiso para editar'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEditMenuDialog(menu)}
                                disabled={!canUpdate}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {showDeleteActions && (
                          <Tooltip title={canDelete ? 'Eliminar menú' : 'Sin permiso para eliminar'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => removeMenu(menu.id)}
                                disabled={!canDelete}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {selectedModule.menu.map((menu) => (
                    <ListItemButton
                      key={menu.id}
                      disableRipple
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': { backgroundColor: 'action.hover' },
                        cursor: 'default',
                      }}
                    >
                      <ListItemText
                        primary={menu.name}
                        secondary={
                          `${menu.route} · Orden ${menu.orden} · Ícono ${menu.icon}` +
                          ` · Acciones ${dedupePermissionActions(menu.permissions || [])
                            .map((permission) => MENU_ACTION_LABELS[permission.action] || permission.name)
                            .join(', ') || 'Visualizar'}`
                        }
                      />
                      {showUpdateActions && (
                        <Tooltip title={canUpdate ? 'Editar menú' : 'Sin permiso para editar'}>
                          <span>
                            <IconButton size="small" onClick={() => openEditMenuDialog(menu)} disabled={!canUpdate}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {showDeleteActions && (
                        <Tooltip title={canDelete ? 'Eliminar menú' : 'Sin permiso para eliminar'}>
                          <span>
                            <IconButton size="small" onClick={() => removeMenu(menu.id)} disabled={!canDelete}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={moduleDialogOpen} onClose={closeModuleDialog} fullWidth maxWidth="sm">
        <DialogTitle>{moduleForm.id ? 'Editar módulo' : 'Nuevo módulo'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Nombre"
              value={moduleForm.name}
              onChange={(event) => setModuleForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Orden"
              type="number"
              value={moduleForm.order}
              onChange={(event) => setModuleForm((prev) => ({ ...prev, order: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Ícono"
              value={moduleForm.icon}
              onChange={(event) => setModuleForm((prev) => ({ ...prev, icon: event.target.value }))}
              fullWidth
              helperText="Ejemplo: ViewModuleIcon"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={moduleForm.visible}
                  onChange={(event) => setModuleForm((prev) => ({ ...prev, visible: event.target.checked }))}
                />
              }
              label="Visible"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModuleDialog}>Cancelar</Button>
          {showModuleSaveAction && (
            <Button onClick={submitModule} variant="contained" disabled={!canSaveModule}>
              Guardar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={menuDialogOpen} onClose={closeMenuDialog} fullWidth maxWidth="sm">
        <DialogTitle>{menuForm.id ? 'Editar menú' : 'Nuevo menú'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Nombre"
              value={menuForm.name}
              onChange={(event) => setMenuForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Ruta"
              value={menuForm.route}
              onChange={(event) => setMenuForm((prev) => ({ ...prev, route: event.target.value }))}
              fullWidth
              helperText="Ejemplo: /administrar/privilegios"
            />
            <TextField
              label="Orden"
              type="number"
              value={menuForm.orden}
              onChange={(event) => setMenuForm((prev) => ({ ...prev, orden: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Ícono"
              value={menuForm.icon}
              onChange={(event) => setMenuForm((prev) => ({ ...prev, icon: event.target.value }))}
              fullWidth
              helperText="Ejemplo: ViewModuleIcon"
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Acciones del menú
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Visualizar se crea siempre. Marca sólo las acciones que realmente aplican para esta pantalla.
              </Typography>
              <FormGroup>
                {availableMenuActionOptions.map((option) => (
                  <FormControlLabel
                    key={option.action}
                    control={
                      <Checkbox
                        checked={menuForm.actions.includes(option.action)}
                        onChange={() => toggleMenuAction(option.action)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMenuDialog}>Cancelar</Button>
          {showMenuSaveAction && (
            <Button onClick={submitMenu} variant="contained" disabled={!canSaveMenu}>
              Guardar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </HeaderResponsive>
  );
};

export default PrivilegiosAdd;
